// services/everpro/everpro-order/list.ts — daftar order di akun Everpro per-brand via S2 proxy
// Endpoint: GET /shipment/v1/orders (list penuh + pagination + filter status/search/sort).
// Dipakai halaman Everpro → section "Data Pengiriman Everpro" (tarik semua + reconcile).
import { everproFetch } from '../everpro-auth';
import type { EverproOrderListData, EverproOrderListItem, EverproOrderListRow } from '../everpro-types';

export interface EverproOrderListParams {
  page?: number;
  limit?: number;
  search?: string;
  statusIds?: number[];
  startDate?: string; // YYYY-MM-DD
  endDate?: string;   // YYYY-MM-DD
  sort?: string;      // contoh: "-created_at" (kolom multi dipisah koma)
  filename?: string;
}

const mapItem = (o: any): EverproOrderListItem => ({
  awbNumber: o.awb_number || '',
  clientOrderNo: o.client_order_no || '',
  shipmentOrderNo: o.shipment_order_no || '',
  status: o.status || '',
  statusId: Number(o.status_id) || 0,
  labelUrl: o.label_url || '',
  courierDetail: o.courier_detail || '',
  packageDesc: o.package_desc || '',
  packagePrice: Number(o.package_price) || 0,
  shipmentPrice: Number(o.shipment_price) || 0,
  totalPrice: Number(o.total_price) || 0,
  receiverName: o.receiver_name || '',
  receiverPhoneNumber: o.receiver_phone_number || '',
  receiverAddress: o.receiver_address || '',
  receiverLocationDetail: o.receiver_location_detail || '',
  shipmentType: o.shipment_type || '',
  pickupTimeDate: o.pickup_time_date || undefined,
  pickupTimeRange: o.pickup_time_range || undefined,
  isCod: !!o.is_cod,
  orderDate: o.order_date || undefined,
});

export async function fetchEverproOrders(
  params: EverproOrderListParams,
  brandId: string,
): Promise<EverproOrderListData> {
  if (!brandId) throw new Error('Everpro: brandId wajib');

  const qs = new URLSearchParams();
  if (params.page != null) qs.append('page', String(params.page));
  if (params.limit != null) qs.append('limit', String(params.limit));
  if (params.search) qs.append('search', params.search);
  if (params.startDate) qs.append('start_date', params.startDate);
  if (params.endDate) qs.append('end_date', params.endDate);
  if (params.filename) qs.append('filename', params.filename);
  if (params.sort) qs.append('sort', params.sort);
  (params.statusIds || []).forEach(id => qs.append('status_id', String(id)));

  const res = await everproFetch(brandId, `/shipment/v1/orders?${qs.toString()}`, { method: 'GET' });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    let message = '';
    try { message = JSON.parse(detail).message || ''; } catch { /* */ }
    throw new Error(message || `Everpro order list: ${res.status} ${res.statusText}`);
  }

  const json = await res.json();
  const d = json?.data ?? {};
  const pag = d.pagination ?? {};
  return {
    orders: (Array.isArray(d.orders) ? d.orders : []).map(mapItem),
    pagination: {
      currentPage: Number(pag.current_page) || 0,
      lastPage: Number(pag.last_page) || 0,
      limit: Number(pag.limit) || 0,
      perPage: Number(pag.per_page) || 0,
      total: Number(pag.total) || 0,
    },
  };
}
